import $ from 'jquery';
import _ from 'underscore';
import Backbone from "backbone";
import AudioFilter from "models/audioEditor/filters/AudioFilter";
import RSVP from "rsvp";

const PRESETS ={};

// Session timeout for RNNoise node reuse (2 seconds)
const SESSION_TIMEOUT_MS = 2000;

const PRESET_IDS = {

}


const CLASSNAME = "RNNoiseFilter";

const RNNoiseFilter =  AudioFilter.extend({
    idAttribute: "id",


    constructor: function(attributes, options) {
        AudioFilter.apply(this, [attributes, options]);
        this.setPresetIdToConfigMap(PRESETS);
        this.set("type", RNNoiseFilter.CLASSNAME);
    },

    isEqual : function(filter){
        if(filter instanceof RNNoiseFilter){
            return (this.isDisabled() == filter.isDisabled()); 
        }
        return false;
    },

    clone : function(){
        const cloneFilter = new RNNoiseFilter();
        cloneFilter.setDisabled(this.isDisabled());
        return cloneFilter;
    },

    
    needToPrepareAudioContext: function(){
        return false;
    },

    prepareAudioContext: function(audioContext){
        return RSVP.Promise.resolve(audioContext);
    },

    _getOrCreateSessionRNNoiseNode: function(audioContext){
        // SOLUTION: Session-based fresh node creation recovers from WASM state corruption
        // - PROBLEM: VOICE track silence during INSERT playback corrupts RNNoise WASM state
        // - Time-gap detection (>2s) indicates potential corruption, triggers fresh node creation
        // - Creates completely new RNNoise nodes (doesn't reset corrupted state)
        // - Reuse within session for performance (same continuous playback)
        // - Export works because OfflineAudioContext is completely isolated from corruption
        // - TESTED: VAD reinitialization alone is NOT sufficient, fresh nodes required
        const currentSession = Date.now();
        
        // If no node exists or it's from a different session (>SESSION_TIMEOUT_MS old), create fresh
        if(!this._rnnoiseNode || 
           !this._sessionTimestamp || 
           (currentSession - this._sessionTimestamp) > SESSION_TIMEOUT_MS ||
           this._rnnoiseNode.context !== audioContext){
            
            // Prevent race condition: check if creation is already in progress
            if(this._nodeCreationPromise){
                return this._nodeCreationPromise;
            }
            
            if(window.RNNoiseNode){
                // Store promise to prevent race condition
                this._nodeCreationPromise = window.RNNoiseNode.register(audioContext).catch((function(error){
                    console.error("Error registering RNNoise to audiocontext. Error:", error);
                    
                    // Fallback to gain node on registration failure
                    console.warn("Falling back to gain node due to RNNoise registration failure");
                    this._cleanupOldNode();
                    const fallbackGainNode = audioContext.createGain();
                    this._sessionTimestamp = currentSession;
                    this._rnnoiseNode = fallbackGainNode;
                    
                    // Mark as fallback for reliable type checking
                    fallbackGainNode._isFallbackGainNode = true;
                    return fallbackGainNode;
                }).bind(this)).then((function(nodeOrFallback){
                    // Use reliable property check instead of fragile constructor.name
                    if(nodeOrFallback && nodeOrFallback._isFallbackGainNode){
                        // Clear promise after fallback handling
                        this._nodeCreationPromise = null;
                        return nodeOrFallback;
                    }
                    
                    const rnnoiseNode = new window.RNNoiseNode(audioContext);
                    
                    // Initialize RNNoise properly
                    if (rnnoiseNode.port) {
                        rnnoiseNode.port.postMessage({ vadProb: 0.5 });
                    }
                    
                    // Clean up old node before storing new one
                    this._cleanupOldNode();
                    
                    // Store for session reuse (set timestamp and node together)
                    this._sessionTimestamp = currentSession;
                    this._rnnoiseNode = rnnoiseNode;
                    
                    // Clear promise only after successful completion
                    this._nodeCreationPromise = null;
                    
                    return rnnoiseNode;
                }).bind(this));
                
                return this._nodeCreationPromise;
            }else{
                // Clean up old node before storing new one
                this._cleanupOldNode();
                
                // Create and store fallback gain node for reuse
                const gainNode = audioContext.createGain();
                this._sessionTimestamp = currentSession;
                this._rnnoiseNode = gainNode;
                return RSVP.Promise.resolve(gainNode);
            }
        } else {
            // Reuse existing node within the same session
            this._sessionTimestamp = currentSession; // Extend session
            return RSVP.Promise.resolve(this._rnnoiseNode);
        }
    },

    _cleanupOldNode: function(){
        // Clean up old node before replacing with new one
        if(this._rnnoiseNode){
            try {
                this._rnnoiseNode.disconnect();
            } catch(e) {
                // Ignore if already disconnected - this is expected in many scenarios
            }
        }
    },

    applyFilterToNode : function(audioNode, audioContext, arrayOfAudioNodesUsed, audioBufferStartTime, audioBufferEndTime, sequencerRendererTracksInfo, rnnoiseNode ){
        if(!this.isDisabled()){
            // CRITICAL: Always create fresh analyzer nodes for proper RNNoise audio flow
            // - RNNoise requires intermediate nodes (cannot connect directly)
            // - Fresh analyzers prevent secondary corruption issues
            const preAnalyser = audioContext.createAnalyser();
            preAnalyser.fftSize = 256;
            
            const postAnalyser = audioContext.createAnalyser();
            postAnalyser.fftSize = 256;
            
            // REQUIRED connection pattern: audioNode → preAnalyser → rnnoiseNode → postAnalyser
            audioNode.connect(preAnalyser);
            preAnalyser.connect(rnnoiseNode);
            rnnoiseNode.connect(postAnalyser);
            
            if(arrayOfAudioNodesUsed){
                arrayOfAudioNodesUsed.push(preAnalyser);
                arrayOfAudioNodesUsed.push(rnnoiseNode);
                arrayOfAudioNodesUsed.push(postAnalyser);
            }
            
            return postAnalyser;
        }else{
            return audioNode;
        }
    },
    
    applyFilter : function(previousOutputNode, audioContext, arrayOfAudioNodesUsed, audioBufferStartTime, audioBufferEndTime, sequencerRendererTracksInfo, audioBuffer){
        if(!this.isDisabled()){
            if(!previousOutputNode){
                previousOutputNode = audioContext.createBufferSource();
                previousOutputNode.buffer = audioBuffer;
                if(arrayOfAudioNodesUsed){
                    arrayOfAudioNodesUsed.push(previousOutputNode);
                }
            }
            return this._getOrCreateSessionRNNoiseNode(audioContext).then((function(rnnoiseNode){
                return this.applyFilterToNode(previousOutputNode, audioContext,arrayOfAudioNodesUsed, audioBufferStartTime,audioBufferEndTime, sequencerRendererTracksInfo, rnnoiseNode);
            }).bind(this));

            
        }else{
            return RSVP.Promise.resolve(previousOutputNode);
        }
    },


});

RNNoiseFilter.CLASSNAME = CLASSNAME;

export default RNNoiseFilter; 